import axios from 'axios';
import { API_GATEWAY_URL } from '../../config/env';
import { TokenResponse } from '../../domain/models/api';
import { OAUTH2_CLIENT_ID, OAUTH2_TOKEN_PATH } from './oauth2Config';

/**
 * Raw calls to api-gw's OAuth2 token endpoint, used for both the
 * authorization_code exchange and the refresh_token grant.
 *
 * Deliberately independent of ApiService (infrastructure/api/ApiService.ts):
 * ApiService's own response interceptor (infrastructure/api/authInterceptor.ts)
 * needs to call refreshAccessToken() on a 401, and application/services/AuthService.ts
 * needs both calls — if this lived as a method on ApiService, one of those two
 * call sites would create an import cycle. This module sits below both.
 *
 * Always relative (`API_GATEWAY_URL`, e.g. "/api"), never the absolute gateway
 * origin: Nginx (prod, nginx/nginx.conf.template) and the dev proxy
 * (src/setupProxy.js) both rewrite `/api/oauth2/token` to api-gw's
 * `/gw/oauth2/token`. No credential is attached anywhere along that path:
 * community_web_app is registered in api-gw's oauth2.json without a client_secret,
 * i.e. a public client, so PKCE's code_verifier is the only proof the token exchange
 * needs — there is no secret to keep out of the browser in the first place.
 */
async function postToken(params: Record<string, string>): Promise<TokenResponse> {
  // Body, never `params`: Spring Authorization Server reads the token request through
  // OAuth2EndpointUtils.getFormParameters(), which skips any parameter whose name appears in
  // the query string. Sent as query params the form map comes back empty and the request fails
  // with invalid_request before client authentication is even reached. Keeping them in the body
  // also stays out of Nginx's access log, where `code`/`code_verifier` would otherwise land.
  const response = await axios.post<TokenResponse>(
    `${API_GATEWAY_URL}/${OAUTH2_TOKEN_PATH}`,
    new URLSearchParams(params).toString(),
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }
  );
  return response.data;
}

export function exchangeAuthorizationCode(
  code: string,
  redirectUri: string,
  codeVerifier: string
): Promise<TokenResponse> {
  return postToken({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: OAUTH2_CLIENT_ID,
    code_verifier: codeVerifier,
  });
}

export function refreshAccessToken(refreshToken: string): Promise<TokenResponse> {
  return postToken({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: OAUTH2_CLIENT_ID,
  });
}
